import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import ImmersiveControls, { useLocalLike } from "../components/ImmersiveControls";
import { Heart, ChevronUp, ChevronDown, Volume2, VolumeX, Store, Play, Pause, X } from "lucide-react";
import SaveButton from "../components/SaveButton";

export default function VideoFeedPage() {
  const nav = useNavigate();
  const [videos, setVideos] = useState([]);
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);
  const [swipeHint, setSwipeHint] = useState(true);
  const touchStartY = useRef(null);
  const touchStartX = useRef(null);
  const touchStartT = useRef(0);
  const wheelCooldown = useRef(false);
  const videoRefs = useRef({});
  // per-item like (persisted)
  const currentId = videos[idx]?._id;
  const [liked, toggleLike] = useLocalLike(`feed_${currentId || "none"}`);

  useEffect(() => {
    (async () => {
      // Merge videos + live streams thumbs into one feed
      const [v, s] = await Promise.all([
        api.get("/videos?limit=30"),
        api.get("/streams?limit=30"),
      ]);
      const feed = [
        ...v.data.map(x => ({ ...x, _type: "video", _id: x.video_id })),
        ...s.data.map(x => ({
          _type: "stream",
          _id: x.stream_id,
          title: x.title,
          thumbnail_url: x.thumbnail_url,
          video_url: x.video_url,
          store_id: x.store_id,
          host_name: x.host_name,
          is_live: x.is_live,
          viewers: x.viewers,
        })),
      ];
      // shuffle for variety
      for (let i = feed.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [feed[i], feed[j]] = [feed[j], feed[i]];
      }
      setVideos(feed);
    })();
    const t = setTimeout(() => setSwipeHint(false), 2600);
    return () => clearTimeout(t);
  }, []);

  // Autoplay current, pause others
  useEffect(() => {
    Object.entries(videoRefs.current).forEach(([key, el]) => {
      if (!el) return;
      if (parseInt(key) === idx) {
        el.play().catch(()=>{});
      } else {
        // Pausing a video element during unmount can throw — safe to ignore.
        try { el.pause(); el.currentTime = 0; } catch (e) {
          if (process.env.NODE_ENV !== "production") console.debug("[feed] pause:", e?.message);
        }
      }
    });
  }, [idx, videos.length]);

  const goTo = useCallback((delta) => {
    if (!videos.length) return;
    setIdx((i) => (i + delta + videos.length) % videos.length);
  }, [videos.length]);

  // keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (["ArrowDown", "ArrowRight", "PageDown", "j"].includes(e.key)) { e.preventDefault(); goTo(1); }
      if (["ArrowUp", "ArrowLeft", "PageUp", "k"].includes(e.key)) { e.preventDefault(); goTo(-1); }
      if (e.key === " ") { e.preventDefault(); togglePause(); }
      if (e.key === "m") setMuted(m => !m);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goTo]);

  const onWheel = (e) => {
    if (wheelCooldown.current) return;
    if (Math.abs(e.deltaY) < 30) return;
    wheelCooldown.current = true;
    setTimeout(() => { wheelCooldown.current = false; }, 700);
    goTo(e.deltaY > 0 ? 1 : -1);
  };
  const onTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartX.current = e.touches[0].clientX;
    touchStartT.current = Date.now();
  };
  const onTouchMove = (e) => {
    // Prevent the browser from rubber-banding / pull-to-refresh while the
    // user is mid-swipe — without this, vertical drags on iOS Safari /
    // Android Chrome fight our handler and the screen barely moves.
    if (touchStartY.current != null) e.preventDefault();
  };
  const onTouchEnd = (e) => {
    if (touchStartY.current == null) return;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    const dx = e.changedTouches[0].clientX - (touchStartX.current ?? 0);
    const elapsed = Date.now() - (touchStartT.current || 0);
    // Loosened thresholds: 50 px drag in 1 s window with vertical-bias
    // accommodates lighter swipes that previously failed on Android.
    if (elapsed < 1000 && Math.abs(dy) > 50 && Math.abs(dy) > Math.abs(dx) * 1.2) {
      goTo(dy < 0 ? 1 : -1);
    }
    touchStartY.current = null; touchStartX.current = null;
  };

  const togglePause = () => {
    const el = videoRefs.current[idx];
    if (!el) return;
    if (el.paused) { el.play(); setPaused(false); }
    else { el.pause(); setPaused(true); }
  };

  if (!videos.length) return <div className="p-10 text-center text-[#A1A1AA]">Loading feed…</div>;
  const cur = videos[idx];

  return (
    <div
      className="fixed inset-0 bg-black overflow-hidden select-none"
      style={{ touchAction: "none", WebkitTouchCallout: "none" }}
      onWheel={onWheel}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      data-testid="video-feed"
    >
      {/* Stacked videos (fade between) */}
      {videos.map((v, i) => {
        const visible = i === idx;
        const near = Math.abs(i - idx) <= 1 || (idx === 0 && i === videos.length-1) || (idx === videos.length-1 && i === 0);
        if (!near) return null;
        return (
          <div
            key={v._id}
            className={`absolute inset-0 transition-opacity duration-300 ${visible?"opacity-100":"opacity-0 pointer-events-none"}`}
          >
            <div className="relative w-full h-full flex items-center justify-center">
              <video
                ref={(el)=>{ videoRefs.current[i] = el; }}
                src={v.video_url}
                poster={v.thumbnail_url}
                playsInline
                loop
                muted={muted}
                onClick={togglePause}
                className="w-full h-full object-cover md:object-contain md:max-w-md md:rounded-2xl"
              />
              {/* Side gradients */}
              <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black to-transparent pointer-events-none"/>
              <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/60 to-transparent pointer-events-none"/>
            </div>
          </div>
        );
      })}

      {/* Top overlay */}
      <div className="absolute top-3 left-4 right-4 flex items-center gap-2 z-20"
        style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <button
          onClick={() => nav(-1)}
          data-testid="feed-exit-btn"
          aria-label="Exit feed"
          className="w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center active:scale-95 hover:bg-[#FF007F]/80">
          <X size={16}/>
        </button>
        <ImmersiveControls
          liked={liked}
          onLike={toggleLike}
          shareTitle={cur.title}
        />
        <div className="ml-auto flex items-center gap-2">
          {cur._type === "stream" && cur.is_live && (
            <span className="live-badge"><span className="dot"/> LIVE</span>
          )}
          <span className="bg-black/60 text-white text-[11px] px-2 py-1 rounded-full font-mono-display">
            {idx+1}/{videos.length}
          </span>
          <button
            data-testid="feed-mute-btn"
            onClick={()=>setMuted(!muted)}
            className="w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center active:scale-95"
          >
            {muted ? <VolumeX size={16}/> : <Volume2 size={16}/>}
          </button>
        </div>
      </div>

      {/* Hint */}
      {swipeHint && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <div className="bg-black/70 text-white px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest animate-pulse">
            Swipe / scroll / ↑↓ to browse
          </div>
        </div>
      )}

      {/* Pause overlay */}
      {paused && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <div className="w-16 h-16 rounded-full bg-black/50 flex items-center justify-center">
            <Play size={28} className="text-white fill-white ml-1"/>
          </div>
        </div>
      )}

      {/* Right side actions */}
      <div className="absolute right-3 flex flex-col items-center gap-4 z-10"
        style={{ bottom: "calc(env(safe-area-inset-bottom) + 96px)" }}>
        <button
          data-testid="feed-like-btn"
          onClick={toggleLike}
          className="flex flex-col items-center"
        >
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${liked?"bg-[#FF007F]":"bg-black/50"} active:scale-90 transition`}>
            <Heart size={22} className={liked?"fill-white text-white":"text-white"}/>
          </div>
          <span className="text-[10px] text-white mt-1 font-bold">Like</span>
        </button>
        {videos[idx] && (
          <div className="flex flex-col items-center">
            <SaveButton kind={videos[idx]._type === "stream" ? "stream" : "video"}
              id={videos[idx]._id} variant="overlay"/>
            <span className="text-[10px] text-white mt-1 font-bold">Save</span>
          </div>
        )}
        <button
          data-testid="feed-prev-btn"
          onClick={()=>goTo(-1)}
          className="w-12 h-12 rounded-full bg-black/50 hover:bg-[#D4FF00] hover:text-black text-white flex items-center justify-center active:scale-90"
        >
          <ChevronUp size={22}/>
        </button>
        <button
          data-testid="feed-next-btn"
          onClick={()=>goTo(1)}
          className="w-12 h-12 rounded-full bg-black/50 hover:bg-[#D4FF00] hover:text-black text-white flex items-center justify-center active:scale-90"
        >
          <ChevronDown size={22}/>
        </button>
      </div>

      {/* Bottom info */}
      <div className="absolute left-4 right-20 z-10"
        style={{ bottom: "calc(env(safe-area-inset-bottom) + 16px)" }}>
        <div className="font-display font-black text-white text-xl sm:text-2xl uppercase leading-tight line-clamp-2">
          {cur.title}
        </div>
        <div className="text-[#D4FF00] text-sm mt-1">
          {cur.host_name ? `@${cur.host_name}` : null}
        </div>
        <div className="flex items-center gap-2 mt-3">
          {cur._type === "stream" ? (
            <Link to={`/live/${cur._id}`} data-testid={`feed-open-live-${cur._id}`} className="btn-primary text-xs px-4 py-2">
              Open live show
            </Link>
          ) : (
            cur.store_id ? (
              <Link to={`/store/${cur.store_id}`} className="btn-secondary text-xs px-4 py-2 flex items-center gap-1">
                <Store size={12}/> Visit store
              </Link>
            ) : null
          )}
        </div>
      </div>
    </div>
  );
}
